import { escapeHtml, positionChartTooltip, useChartLifecycle } from "@nexus/widget-kit";
export { escapeHtml } from "@nexus/widget-kit";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import * as echarts from "echarts/core";
import { SankeyChart, type SankeySeriesOption } from "echarts/charts";
import { TooltipComponent, type TooltipComponentOption } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { Select } from "@nexus/ui";
import { defineLabels, firstError, resolveChartColors, resolveLabels, unwrap, UpdatingPill, Widget, WidgetContentReadyContext, WidgetFullscreenContext, WidgetState, withAlpha, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import type { ChartColors, WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { SUPPLY_CHAIN_CONTRACT, SUPPLY_CHAIN_STAGES } from "./types";
import type { SupplyChainData, SupplyChainNode } from "./types";
import { StageFunnel } from "./StageFunnel";

echarts.use([SankeyChart, TooltipComponent, CanvasRenderer]);

type SankeyOption = echarts.ComposeOption<SankeySeriesOption | TooltipComponentOption>;

export interface SupplyChainSankeyConfig {
  title?: string;
  /** Per-instance overrides of the texts listed in `manifest.labels`. */
  labels?: Record<string, string>;
  /**
   * `"simple"` (default) shows one block per stage, sized by how many distinct
   * entities it holds, joined by tapering flows — a light read for a war-room
   * screen. `"detailed"` is the full per-node flow with a stage legend and a
   * focus selector. Anything other than `"detailed"` is treated as `"simple"`.
   */
  viewMode?: "simple" | "detailed";
}

export const LABELS = defineLabels([
  { key: "stagePirateService", name: "Stage: pirate services", default: "Pirate services" },
  { key: "stageWatermarkId", name: "Stage: watermark IDs", default: "Watermark IDs" },
  { key: "stageCdnProvider", name: "Stage: CDN providers", default: "CDN providers" },
  { key: "stagePirateHost", name: "Stage: pirate hosts", default: "Pirate hosts" },
  { key: "stageAccount", name: "Stage: accounts", default: "Accounts" },
  { key: "stageOutcome", name: "Stage: outcomes", default: "Outcomes" },
  {
    key: "flowName",
    name: "Flow name",
    default: "streams",
    hint: "What the values of the flows measure, e.g. streams.",
  },
  { key: "focusLabel", name: "Focus selector", default: "Focus on", advanced: true },
  { key: "focusNone", name: "Focus selector: no focus", default: "Everything", advanced: true },
  { key: "emptyTitle", name: "Empty title", default: "No supply chain data" },
  {
    key: "emptyDescription",
    name: "Empty description",
    default: "No flows were reported for this period.",
  },
] as const);

export type ChainLabels = ReturnType<typeof resolveLabels<typeof LABELS>>;
const DEFAULT_LABELS: ChainLabels = resolveLabels(LABELS, undefined);

export const manifest: WidgetManifest = {
  id: "supply-chain-sankey",
  name: "Supply Chain Sankey",
  description:
    "A supply chain read left to right: how many distinct entities each stage holds, or every flow in detail with a focus on one node.",
  version: "1.0.0",
  labels: LABELS,
  dataRequests: {
    chain: {
      source: "ott-piracy-chain",
      contract: SUPPLY_CHAIN_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: SupplyChainSankeyConfig = {
  title: "Supply chain",
  viewMode: "simple",
};

/** Anything other than the explicit `"detailed"` opt-in reads as the light default. */
export function resolveViewMode(config: SupplyChainSankeyConfig): "simple" | "detailed" {
  return config.viewMode === "detailed" ? "detailed" : "simple";
}

const fontBase = { fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" };
const countFormat = new Intl.NumberFormat("en");

export function formatValue(value: number): string {
  return countFormat.format(Math.round(value));
}

// ── Graph ─────────────────────────────────────────────────────────────────────

export interface ChainGraph {
  /** Ordered by stage, then as received. */
  nodes: SupplyChainNode[];
  links: { source: string; target: string; value: number }[];
  /** Stages present in the data, in flow order. */
  stages: string[];
}

export function stageRank(stage: string): number {
  const known = (SUPPLY_CHAIN_STAGES as readonly string[]).indexOf(stage);
  return known === -1 ? SUPPLY_CHAIN_STAGES.length : known;
}

/** Keeps only what can be drawn: known endpoints, positive values, no repeated node or link. */
export function buildGraph(data: SupplyChainData): ChainGraph {
  const seen = new Set<string>();
  const nodes = data.nodes
    .filter((node) => node.value > 0 && !seen.has(node.id) && seen.add(node.id))
    .map((node, position) => ({ node, position }))
    .sort((a, b) => stageRank(a.node.stage) - stageRank(b.node.stage) || a.position - b.position)
    .map(({ node }) => node);
  const known = new Set(nodes.map((node) => node.id));
  const linkKeys = new Set<string>();
  const links = data.links.filter((link) => {
    const key = `${link.source}\u0000${link.target}`;
    if (link.value <= 0 || !known.has(link.source) || !known.has(link.target) || linkKeys.has(key))
      return false;
    linkKeys.add(key);
    return true;
  });
  const stages = [...new Set(nodes.map((node) => node.stage))].sort(
    (a, b) => stageRank(a) - stageRank(b),
  );
  return { nodes, links, stages };
}

/**
 * Everything upstream and downstream of a node, transitively: the path every
 * stream through it took. Used for the focus, so the same set is lit whether the
 * node was chosen by click or by the selector.
 */
export function connectedTo(graph: ChainGraph, id: string) {
  const nodes = new Set([id]);
  const links = new Set<ChainGraph["links"][number]>();
  const walk = (direction: "forward" | "backward") => {
    const queue = [id];
    const visited = new Set(queue);
    while (queue.length > 0) {
      const current = queue.shift() as string;
      for (const link of graph.links) {
        const from = direction === "forward" ? link.source : link.target;
        const to = direction === "forward" ? link.target : link.source;
        if (from !== current) continue;
        links.add(link);
        nodes.add(to);
        if (!visited.has(to)) {
          visited.add(to);
          queue.push(to);
        }
      }
    }
  };
  walk("forward");
  walk("backward");
  return { nodes, links };
}

export function isOtherNode(node: SupplyChainNode): boolean {
  return node.id.endsWith(":other");
}

export function stageLabel(stage: string, labels: ChainLabels): string {
  const byStage: Record<string, string> = {
    PIRATE_SERVICE: labels.stagePirateService,
    WATERMARK_ID: labels.stageWatermarkId,
    CDN_PROVIDER: labels.stageCdnProvider,
    PIRATE_HOST: labels.stagePirateHost,
    ACCOUNT: labels.stageAccount,
    OUTCOME: labels.stageOutcome,
  };
  return byStage[stage] ?? stage;
}

export function stageColor(colors: ChartColors, stage: string, graph: ChainGraph): string {
  const index = graph.stages.indexOf(stage);
  return colors.series[Math.max(0, index) % colors.series.length];
}

export function describeChain(graph: ChainGraph, labels: ChainLabels): string {
  if (graph.nodes.length === 0) return "No supply chain data.";
  const first = graph.nodes.filter((node) => node.stage === graph.stages[0]);
  const total = first.reduce((sum, node) => sum + node.value, 0);
  return `Flow of ${formatValue(total)} ${labels.flowName} across ${graph.stages.length} stages: ${graph.stages.map((stage) => stageLabel(stage, labels)).join(", ")}.`;
}

// ── Chart option ──────────────────────────────────────────────────────────────

export function buildOption(
  graph: ChainGraph,
  colors: ChartColors,
  options?: { labels?: ChainLabels; focusId?: string | null },
): SankeyOption {
  const labels = options?.labels ?? DEFAULT_LABELS;
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  const lit =
    options?.focusId && byId.has(options.focusId) ? connectedTo(graph, options.focusId) : null;
  const lastStage = graph.stages[graph.stages.length - 1];

  return {
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
      triggerOn: "mousemove",
      appendTo: () => document.body,
      transitionDuration: 0,
      position: (point, _params, _dom, _rect, size) => positionChartTooltip(point, size),
      backgroundColor: colors.tooltipBg,
      borderWidth: 0,
      borderRadius: 8,
      padding: [10, 12],
      textStyle: {
        color: colors.tooltipText,
        fontSize: colors.typography?.labelSize ?? 12,
        ...fontBase,
      },
      formatter: (params: unknown) => {
        const p = params as {
          dataType?: string;
          name?: string;
          value?: number;
          data?: { source?: string; target?: string };
        };
        if (p.dataType === "edge") {
          const source = byId.get(p.data?.source ?? "");
          const target = byId.get(p.data?.target ?? "");
          return `${escapeHtml(source?.label ?? "")} → ${escapeHtml(target?.label ?? "")}<br><b>${formatValue(p.value ?? 0)}</b> ${escapeHtml(labels.flowName)}`;
        }
        const node = byId.get(p.name ?? "");
        if (!node) return "";
        return `<b>${escapeHtml(node.label)}</b><br><span style="opacity:0.75">${escapeHtml(stageLabel(node.stage, labels))}</span><br><b>${formatValue(node.value)}</b> ${escapeHtml(labels.flowName)}`;
      },
    },
    series: [
      {
        type: "sankey",
        left: 8,
        right: 8,
        top: 12,
        bottom: 12,
        nodeGap: 8,
        nodeWidth: 14,
        // Nodes arrive ordered by stage then by weight: keep that order instead of re-sorting.
        layoutIterations: 0,
        emphasis: { focus: "adjacency" },
        lineStyle: { color: "gradient", opacity: 0.42, curveness: 0.5 },
        label: {
          color: colors.tooltipText,
          fontSize: colors.typography?.labelSize ?? 12,
          ...fontBase,
        },
        itemStyle: { borderWidth: 0 },
        data: graph.nodes.map((node) => {
          const base = isOtherNode(node)
            ? withAlpha(colors.label, 0.5)
            : stageColor(colors, node.stage, graph);
          const dimmed = lit !== null && !lit.nodes.has(node.id);
          return {
            // The id is the name ECharts joins links on; the label is what is drawn.
            name: node.id,
            depth: graph.stages.indexOf(node.stage),
            value: node.value,
            label: {
              show: !dimmed,
              position: node.stage === lastStage ? "left" : "right",
              formatter: node.label,
            },
            itemStyle: { color: dimmed ? withAlpha(base, 0.2) : base, borderWidth: 0 },
          };
        }),
        links: graph.links.map((link) => ({
          ...link,
          lineStyle: { opacity: lit ? (lit.links.has(link) ? 0.5 : 0.06) : 0.42 },
        })),
      },
    ],
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

function StageLegend({
  graph,
  colors,
  labels,
}: {
  graph: ChainGraph;
  colors: ChartColors | null;
  labels: ChainLabels;
}) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
      {graph.stages.map((stage) => (
        <li key={stage} className="flex items-center gap-1.5">
          <span
            aria-hidden
            className="size-2"
            style={{ backgroundColor: colors ? stageColor(colors, stage, graph) : "currentColor" }}
          />
          {stageLabel(stage, labels)}
        </li>
      ))}
    </ul>
  );
}

/** The full per-node flow, drawn with ECharts, with a stage legend and a keyboard-friendly focus selector. */
function DetailedChain({
  graph,
  labels,
  description,
  onRendered,
}: {
  graph: ChainGraph;
  labels: ChainLabels;
  description: string;
  onRendered: () => void;
}) {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [colors, setColors] = useState<ChartColors | null>(null);
  // A focus on a node the new data no longer has must not linger.
  const activeFocus = focusId && graph.nodes.some((node) => node.id === focusId) ? focusId : null;
  const focusNode = graph.nodes.find((node) => node.id === activeFocus) ?? null;

  const graphRef = useRef(graph);
  const labelsRef = useRef(labels);
  const focusRef = useRef(activeFocus);
  useEffect(() => {
    graphRef.current = graph;
    labelsRef.current = labels;
    focusRef.current = activeFocus;
  }, [graph, labels, activeFocus]);

  useEffect(() => {
    const host = chartRef.current;
    if (!host) return;
    if (!chartInstanceRef.current || chartInstanceRef.current.isDisposed()) {
      const chart = echarts.init(host, null, { renderer: "canvas" });
      chart.on("click", (params: unknown) => {
        const p = params as { dataType?: string; name?: string };
        if (p.dataType !== "node" || !p.name) return;
        const id = p.name;
        setFocusId((current) => (current === id ? null : id));
      });
      chartInstanceRef.current = chart;
    }
    const resolved = resolveChartColors(host);
    setColors(resolved);
    chartInstanceRef.current.setOption(
      buildOption(graph, resolved, { labels, focusId: activeFocus }),
      true,
    );
    onRendered();
  }, [graph, labels, activeFocus, onRendered]);

  useChartLifecycle(chartRef, chartInstanceRef, (host, chart) => {
    const resolved = resolveChartColors(host);
    setColors(resolved);
    chart.setOption(
      buildOption(graphRef.current, resolved, {
        labels: labelsRef.current,
        focusId: focusRef.current,
      }),
      true,
    );
  });

  const focusOptions = useMemo(
    () =>
      graph.nodes.map((node) => ({
        value: node.id,
        label: `${stageLabel(node.stage, labels)} · ${node.label}`,
      })),
    [graph, labels],
  );

  return (
    <>
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 pt-3">
        <StageLegend graph={graph} colors={colors} labels={labels} />
        <div className="w-full @2xl/widget:w-64">
          {/* The keyboard and screen-reader way to focus a node: the chart itself is pointer-only. */}
          <Select
            label={labels.focusLabel}
            value={activeFocus ?? ""}
            emptyLabel={labels.focusNone}
            options={focusOptions}
            onValueChange={(value) => setFocusId(value === "" ? null : value)}
            controlSize="sm"
          />
        </div>
      </div>
      <div className="relative min-h-[30rem] w-full flex-1">
        <div
          ref={chartRef}
          role="img"
          aria-label={description}
          className="absolute inset-0 h-full w-full"
        />
      </div>
      <p aria-live="polite" className="shrink-0 px-4 pb-3 pt-2 text-xs text-muted">
        {focusNode
          ? `${stageLabel(focusNode.stage, labels)} · ${focusNode.label} · ${formatValue(focusNode.value)} ${labels.flowName}`
          : "Click a node, or use the selector, to follow its flows."}
      </p>
    </>
  );
}

function SupplyChainSankeyComponent({ data, config }: WidgetProps<SupplyChainSankeyConfig>) {
  const { markContentReady } = useContext(WidgetContentReadyContext);
  const { fullscreen: inFocusOverlay } = useContext(WidgetFullscreenContext);
  const hasRenderedRef = useRef(false);

  const error = firstError(data, ["chain"]);
  const source = unwrap<SupplyChainData>(data.chain);
  const isLoading = data.chain?.isLoading ?? false;
  const title = config.title?.trim() || defaultConfig.title || manifest.name;
  const labelsJson = JSON.stringify(resolveLabels(LABELS, config));
  const labels = useMemo(() => JSON.parse(labelsJson) as ChainLabels, [labelsJson]);
  const simple = resolveViewMode(config) === "simple";

  const graph = useMemo(() => (source ? buildGraph(source) : null), [source]);
  const isEmpty = !isLoading && !error && (!graph || graph.nodes.length === 0);
  const ready = !error && !isEmpty && !isLoading && graph !== null && graph.nodes.length > 0;
  const firstLoad = isLoading && !hasRenderedRef.current;
  const description = graph ? describeChain(graph, labels) : "Supply chain chart";

  const onRendered = useMemo(
    () => () => {
      hasRenderedRef.current = true;
      markContentReady();
    },
    [markContentReady],
  );
  useEffect(() => {
    if (ready && simple) onRendered();
  }, [ready, simple, onRendered, graph]);

  const state = error ? (
    <WidgetIssueState error={error} className="absolute inset-0" />
  ) : isEmpty ? (
    <WidgetState
      title={labels.emptyTitle}
      description={labels.emptyDescription}
      className="absolute inset-0"
    />
  ) : firstLoad ? (
    <WidgetSkeleton shape="chart" className="absolute inset-0" />
  ) : null;

  return (
    <Widget
      title={title}
      intrinsicHeight={!inFocusOverlay}
      actions={isLoading && !firstLoad ? <UpdatingPill /> : undefined}
    >
      <div
        className={`flex w-full flex-col ${inFocusOverlay ? "h-full min-h-0 overflow-y-auto" : "flex-1"}`}
      >
        {graph &&
          graph.nodes.length > 0 &&
          !error &&
          (simple ? (
            <div className="flex flex-1 flex-col justify-center px-4 pb-4 pt-5 @2xl/widget:px-6">
              <StageFunnel graph={graph} labels={labels} description={description} />
            </div>
          ) : (
            <DetailedChain
              graph={graph}
              labels={labels}
              description={description}
              onRendered={onRendered}
            />
          ))}
        {state && (
          <div className={`relative w-full flex-1 ${simple ? "min-h-[18rem]" : "min-h-[30rem]"}`}>
            {state}
          </div>
        )}
      </div>
    </Widget>
  );
}

export const Component = SupplyChainSankeyComponent;

const widgetModule: WidgetModule<SupplyChainSankeyConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
